import { memo, useEffect, useState } from "react";
import { Chip, Stack } from "@mui/material";
import { ViewHeader } from "../components/ViewHeader";
import { ReissueSelectPatientStep } from "./ReissueSelectPatientStep";
import { ReissueSelectDocumentStep } from "./ReissueSelectDocumentStep";
import type { ReissuePatientOption } from "./reissuePatientId";

type WizardStep = "patient" | "document";

interface ScheduleDocumentReissuePanelProps {
  /** Quando false (troca de card na agenda), reseta o wizard. */
  isActive: boolean;
}

const STEP_LABELS: Array<{ id: WizardStep; label: string }> = [
  { id: "patient", label: "1. Paciente" },
  { id: "document", label: "2. Documento / Reemitir" },
];

/**
 * Atalho inline na agenda: paciente → tipo/lista de documentos → reemissão
 * (reutiliza as mesmas regras do DocumentReissueShortcutDialog).
 */
function ScheduleDocumentReissuePanel({
  isActive,
}: ScheduleDocumentReissuePanelProps) {
  const [step, setStep] = useState<WizardStep>("patient");
  const [selectedPatient, setSelectedPatient] =
    useState<ReissuePatientOption | null>(null);

  // Evita estado stale ao sair do card da agenda
  useEffect(() => {
    if (!isActive) {
      setStep("patient");
      setSelectedPatient(null);
    }
  }, [isActive]);

  const handleSelectPatient = (patient: ReissuePatientOption) => {
    if (!patient?.patientId) return;
    setSelectedPatient(patient);
    setStep("document");
  };

  const handleBackToPatient = () => {
    setStep("patient");
    // Mantém o paciente selecionado para facilitar nova busca; limpa só o passo de docs
  };

  return (
    <Stack direction="column" width="100%" spacing={2}>
      <ViewHeader title="Reemitir Documentos" titleOnly />

      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        {STEP_LABELS.map((item) => {
          const active = step === item.id;
          const completed =
            item.id === "patient" && step === "document" && !!selectedPatient;
          return (
            <Chip
              key={item.id}
              label={item.label}
              color={active || completed ? "primary" : "default"}
              variant={active ? "filled" : "outlined"}
              onClick={() => {
                // Voltar ao passo 1 é sempre permitido; avançar só com paciente
                if (item.id === "patient") {
                  setStep("patient");
                  return;
                }
                if (selectedPatient?.patientId) setStep("document");
              }}
              clickable={item.id === "patient" || !!selectedPatient?.patientId}
              sx={{ fontWeight: active ? 600 : 400 }}
            />
          );
        })}
      </Stack>

      {step === "patient" ? (
        <ReissueSelectPatientStep
          selectedPatient={selectedPatient}
          onSelectAndContinue={handleSelectPatient}
        />
      ) : selectedPatient?.patientId ? (
        <ReissueSelectDocumentStep
          patientId={selectedPatient.patientId}
          patientName={selectedPatient.name}
          onBackToPatient={handleBackToPatient}
        />
      ) : null}
    </Stack>
  );
}

export default memo(ScheduleDocumentReissuePanel);
